'use client';

import { useState } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Link from 'next/link';

import { Button } from '@write4me/ui/components/button';
import { Input } from '@write4me/ui/components/input';
import { PasswordInput } from '@write4me/ui/components/password-input';
import { Select } from '@write4me/ui/components/select';
import { GoogleSignInButton } from '@/components/auth/google-sign-in-button';
import { useToast } from '@write4me/ui/components';
import { marketingUrl } from '@write4me/ui/site-url';
import { useSignUp } from '@/lib/query/hooks/use-auth-mutations';
import { signUpSchema, type SignUpInput } from '@/lib/validations/auth';

const ROLE_OPTIONS = [
  { value: 'none', label: 'None - select a role to continue' },
  { value: 'employer', label: 'Employer - I want to hire writers' },
  { value: 'writer', label: 'Writer - I want to earn from writing' },
];

export function SignUpForm() {
  const router = useRouter();
  const { toast } = useToast();
  const searchParams = useSearchParams();
  const inviteToken = searchParams.get('invite');
  const showMissingRoleBanner = searchParams.get('missing_role') === '1';
  // Cross-domain CTAs from the marketing site (write4me.co) pass the chosen
  // audience as `?role=employer|writer` so the form opens pre-selected.
  const roleParam = searchParams.get('role');
  const initialRole =
    roleParam === 'employer' || roleParam === 'writer' ? roleParam : 'none';
  const signUpMutation = useSignUp();

  const [serverError, setServerError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SignUpInput>({
    resolver: zodResolver(signUpSchema),
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: '',
      role: initialRole as unknown as SignUpInput['role'],
      terms_accepted: false as unknown as true,
    },
  });

  const selectedRole = watch('role');
  const termsAccepted = watch('terms_accepted');
  const isRoleSelected = selectedRole === 'employer' || selectedRole === 'writer';
  const canProceed = isRoleSelected && termsAccepted === true;

  async function onSubmit(data: SignUpInput) {
    if (data.role !== 'employer' && data.role !== 'writer') {
      const msg = 'Please select a valid role to continue.';
      setServerError(msg);
      toast.error(msg);
      return;
    }

    if (data.terms_accepted !== true) {
      const msg = 'You must accept the terms and conditions to continue.';
      setServerError(msg);
      toast.error(msg);
      return;
    }

    setIsSubmitting(true);
    setServerError(null);

    try {
      const result = await signUpMutation.mutateAsync({
        email: data.email,
        password: data.password,
        role: data.role,
        termsAccepted: true,
      });

      if (result.session) {
        // Email auto-confirmed — redirect to onboarding immediately
        const onboardingUrl = inviteToken
          ? `/onboarding?invite=${inviteToken}`
          : '/onboarding';
        router.push(onboardingUrl);
        return;
      }

      // Email confirmation required — redirect to login with confirmation alert
      router.push('/login?confirmed=pending');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'An error occurred during sign up.';
      setServerError(msg);
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  }

  // Build the redirect URL for Google sign-in, preserving invite token
  const googleRedirectTo = inviteToken
    ? `/onboarding?invite=${inviteToken}`
    : undefined;

  return (
    <div className="space-y-6">
      {showMissingRoleBanner && (
        <div
          className="rounded-[12px] bg-secondary-container border border-secondary/20 px-4 py-3 text-sm font-ui text-ink"
          role="status"
        >
          Please choose how you&apos;d like to use Write4Me before continuing.
        </div>
      )}

      <form
        method="post"
        action="/signup"
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-6"
        noValidate
      >
        {/* Preserve invite token as a hidden field */}
        {inviteToken && (
          <input type="hidden" name="invite" value={inviteToken} />
        )}

        <Select
          label="I am a"
          options={ROLE_OPTIONS}
          error={errors.role?.message}
          {...register('role')}
        />

        {/* Terms & Conditions */}
        <div className="flex flex-col gap-1">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              {...register('terms_accepted')}
              className="mt-0.5 h-4 w-4 rounded border-outline-variant text-primary accent-primary cursor-pointer"
            />
            <span className="text-sm font-ui text-ink-secondary">
              I agree to the{' '}
              <a
                href={marketingUrl('/terms')}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline hover:text-primary/80"
              >
                Terms and Conditions
              </a>
            </span>
          </label>
          {errors.terms_accepted?.message && (
            <p className="text-xs font-ui text-error" role="alert">
              {errors.terms_accepted.message}
            </p>
          )}
        </div>

        <GoogleSignInButton
          redirectTo={googleRedirectTo}
          disabled={!canProceed}
          pendingRole={isRoleSelected ? selectedRole : undefined}
        />

        {/* Separator */}
        <div className="flex items-center gap-4">
          <div className="flex-1 h-px bg-outline-variant" />
          <span className="text-sm text-ink-muted font-ui">
            or continue with email
          </span>
          <div className="flex-1 h-px bg-outline-variant" />
        </div>

        {/* Inline server error (kept for accessibility; toast is additive) */}
        {serverError && (
          <div
            className="rounded-[12px] bg-primary-fixed border border-primary-light-border px-4 py-3 text-sm font-ui text-error"
            role="alert"
          >
            {serverError}
          </div>
        )}

        <div className="space-y-4">
          <Input
            label="Email"
            type="email"
            placeholder="you@example.com"
            autoComplete="email"
            disabled={!canProceed}
            error={errors.email?.message}
            {...register('email')}
          />

          <PasswordInput
            label="Password"
            placeholder="At least 8 characters"
            autoComplete="new-password"
            disabled={!canProceed}
            error={errors.password?.message}
            {...register('password')}
          />

          <PasswordInput
            label="Confirm password"
            placeholder="Repeat your password"
            autoComplete="new-password"
            disabled={!canProceed}
            error={errors.confirmPassword?.message}
            {...register('confirmPassword')}
          />

          <Button
            type="submit"
            variant="primary"
            size="lg"
            loading={isSubmitting}
            disabled={!canProceed}
            className="w-full"
          >
            Create account
          </Button>
        </div>
      </form>

      <p className="text-center text-sm font-ui text-ink-secondary">
        Already have an account?{' '}
        <Link
          href="/login"
          className="text-primary font-medium hover:underline"
        >
          Sign in
        </Link>
      </p>
    </div>
  );
}
